import { computed } from "vue";
import { useUserStore } from "../../../../Stores/User";
import { useConversation } from "../../../../Stores/Conversation";
import { isRevokedInactiveInviteProfile, isDeletedInviteProfile } from "../../../../Composables/useTeamMembers";
import { filterPeople, matchesPeopleSearch } from "./peopleSearch";

function isInvitedPerson(profile) {
    return profile?.is_organization_user === false && ! isDeletedInviteProfile(profile);
}

function matchesGroupSearch(group, query) {
    if (!query) {
        return true;
    }

    return group?.name?.toLowerCase().includes(query.toLowerCase());
}

function filterPendingInvites(people, query) {
    return filterPeople(people, query);
}

function filterGroups(groups, query) {
    return groups
        .filter((group) => matchesGroupSearch(group, query))
        .sort((left, right) => left.name.localeCompare(right.name));
}

export {
    filterPeople,
    filterPendingInvites,
    filterGroups,
    matchesPeopleSearch,
    matchesGroupSearch,
};

export function useNewDirectoryData(search) {
    const userStore = useUserStore();
    const conversationStore = useConversation();

    const organizationId = computed(() => userStore.profile?.organization_id);

    const profiles = computed(() =>
        userStore.profiles.filter(
            (profile) =>
                profile?.organization_id === organizationId.value &&
                !profile?.is_agent &&
                !isDeletedInviteProfile(profile),
        ),
    );

    const registeredPeople = computed(() =>
        profiles.value.filter(
            (profile) =>
                profile?.is_organization_user && !isInvitedPerson(profile),
        ),
    );

    const associatedPeople = computed(() =>
        registeredPeople.value.filter(
            (profile) => profile?.is_active || profile?.non_registered,
        ),
    );

    const disassociatedPeople = computed(() =>
        registeredPeople.value.filter(
            (profile) => !profile?.non_registered && !profile?.is_active,
        ),
    );

    const pendingInvites = computed(() =>
        profiles.value.filter(
            (profile) =>
                isInvitedPerson(profile) &&
                !isRevokedInactiveInviteProfile(profile),
        ),
    );

    const revokedInvites = computed(() =>
        profiles.value.filter(
            (profile) =>
                isInvitedPerson(profile) &&
                isRevokedInactiveInviteProfile(profile),
        ),
    );

    const invitedPeople = computed(() =>
        profiles.value.filter((profile) => isInvitedPerson(profile)),
    );

    const allGroups = computed(() => {
        const groups = conversationStore.group_conversations ?? [];

        return [
            ...groups.filter((group) => !group.is_archived),
            ...groups.filter((group) => group.is_archived),
        ];
    });

    const groups = computed(() =>
        allGroups.value.filter((group) => !group?.state?.is_one_off),
    );

    const topics = computed(() =>
        allGroups.value.filter((group) => group?.state?.is_one_off),
    );

    const canShowInvites = computed(
        () =>
            userStore.isAuthorizedFor("invitation-edit") &&
            userStore.canInviteToOrganization(),
    );

    const searchQuery = computed(() => search.value.trim());

    const filteredAssociatedPeople = computed(() =>
        filterPeople(associatedPeople.value, searchQuery.value),
    );

    const filteredDisassociatedPeople = computed(() =>
        filterPeople(disassociatedPeople.value, searchQuery.value),
    );

    const filteredRegisteredPeople = computed(() =>
        filterPeople(registeredPeople.value, searchQuery.value),
    );

    const filteredGroups = computed(() =>
        filterGroups(groups.value, searchQuery.value),
    );

    const filteredTopics = computed(() =>
        filterGroups(topics.value, searchQuery.value),
    );

    const filteredPendingInvites = computed(() =>
        filterPendingInvites(pendingInvites.value, searchQuery.value),
    );

    const filteredRevokedInvites = computed(() =>
        filterPeople(revokedInvites.value, searchQuery.value),
    );

    const searchResultCount = computed(() => {
        if (!searchQuery.value) {
            return 0;
        }

        const inviteCount = canShowInvites.value
            ? filteredPendingInvites.value.length +
              filteredRevokedInvites.value.length
            : 0;

        return (
            filteredAssociatedPeople.value.length +
            filteredDisassociatedPeople.value.length +
            filteredGroups.value.length +
            filteredTopics.value.length +
            inviteCount
        );
    });

    const searchResultText = computed(() => {
        const count = searchResultCount.value;

        return `${count} result${count !== 1 ? "s" : ""}`;
    });

    return {
        profiles,
        associatedPeople,
        disassociatedPeople,
        registeredPeople,
        pendingInvites,
        revokedInvites,
        invitedPeople,
        filteredAssociatedPeople,
        filteredDisassociatedPeople,
        filteredRegisteredPeople,
        filteredPendingInvites,
        filteredRevokedInvites,
        filteredGroups,
        filteredTopics,
        groups,
        topics,
        canShowInvites,
        searchResultText,
        searchResultCount,
    };
}
